import { action, runInAction, makeObservable, observable } from 'mobx';
import { eventBus, subscribe } from 'mobx-event-bus2';
import { AxiosError } from 'axios';

import { reverse } from 'named-urls';

import Logger from 'utils/logger';

import API from 'utils/api';
import { EventType } from 'utils/events/constants';
import { AnswerTypes } from 'utils/api/modules/incidents/incidents.types';
import { EntityValueType } from 'utils/api/modules/entities/entities.types';
import { isConflict, isNotFound } from 'utils/api/errors';
import { conflictDetail, fieldErrorsOf } from 'utils/library';

import { EntityModules } from 'core/constants';

import routes from 'core/routes';

import EntitiesActivities from 'stores/EntitiesActivities';

import RootStore from '../Root';
import EntityDetails from '../EntityDetails/EntityDetails';

export default class HazardsDetailsStore extends EntityDetails {
  @observable deleteBlockedReason?: string;

  private loadingHazardId?: string;

  constructor(rootStore: RootStore, api: typeof API) {
    super(rootStore, api, EntityModules.Hazards, routes.dashboard.risks.hazards.toString(), (uuid: string): string =>
      reverse(routes.dashboard.risks.hazardDetails, { entityId: uuid })
    );

    makeObservable(this);

    eventBus.register(this);
  }

  @action.bound
  async loadDetails(hazardId: string): Promise<void> {
    this.isLoaded = false;
    this.deleteBlockedReason = undefined;
    this.loadingHazardId = hazardId;
    try {
      const { data } = await this.api.entities.loadEntitiesDetails(this.entityType, hazardId)();
      if (this.loadingHazardId !== hazardId) {
        return;
      }
      runInAction(() => {
        this.activities = new EntitiesActivities(this.store, this.api, hazardId, this.entityType);
        this.error = undefined;
        this.details = data;
      });
    } catch (e) {
      if (this.loadingHazardId !== hazardId) {
        return;
      }
      runInAction(() => {
        this.error = e as AxiosError;
      });
      if (!isNotFound(e as AxiosError)) {
        Logger.error(`Invalid load hazard details API response. Hazard id: ${hazardId}`, e);
      }
    } finally {
      if (this.loadingHazardId === hazardId) {
        runInAction(() => {
          this.isLoaded = true;
        });
      }
    }
  }

  create = (name: string): Promise<boolean | string> => this.createHazard(name);

  createHazard = async (
    name: string,
    values: Record<string, string | string[] | null> = {}
  ): Promise<boolean | string> => {
    runInAction(() => {
      this.fieldErrors = {};
    });
    try {
      const {
        data: { uuid },
      } = await this.api.entities.createEntity(this.entityType, { [`${this.prefix}name`]: name, ...values })();
      this.store.notification.enqueueSuccessSnackbar('Hazard added');
      return uuid;
    } catch (e) {
      const fieldErrors = fieldErrorsOf(e);
      runInAction(() => {
        this.fieldErrors = fieldErrors;
      });
      if (Object.keys(fieldErrors).length === 0) {
        Logger.error('Failed to create hazard', e);
        this.store.notification.enqueueErrorSnackbar('The hazard could not be added. Something went wrong.');
      }
      return false;
    }
  };

  delete = async (hazardId: string): Promise<boolean> => {
    runInAction(() => {
      this.deleteBlockedReason = undefined;
    });
    try {
      await this.api.entities.deleteEntity(this.entityType, hazardId)();
    } catch (e) {
      if (isConflict(e as AxiosError)) {
        runInAction(() => {
          this.deleteBlockedReason = conflictDetail(e) || 'This hazard is in use.';
        });
      } else {
        Logger.error('Failed to delete hazard', e);
      }
      return false;
    }
    return true;
  };

  edit = async (data: Record<string, EntityValueType>): Promise<boolean | string[]> => {
    if (this.details?.uuid === undefined) {
      return false;
    }
    const hazardId = this.details.uuid;
    runInAction(() => {
      this.fieldErrors = {};
    });
    try {
      const edited = await this.api.entities.editEntity(this.entityType, hazardId, data)();
      runInAction(() => {
        if (this.details?.uuid === hazardId) {
          this.details = edited.data;
          this.isContentEdited = false;
        }
      });
      this.store.notification.enqueueSuccessSnackbar('Hazard edited successfully');
      return true;
    } catch (e) {
      const fieldErrors = fieldErrorsOf(e);
      if (Object.keys(fieldErrors).length > 0) {
        runInAction(() => {
          this.fieldErrors = fieldErrors;
        });
        return Object.keys(fieldErrors);
      }
      if (isConflict(e as AxiosError)) {
        this.store.notification.enqueueErrorSnackbar(
          conflictDetail(e) || "An archived hazard can't be edited. Unarchive it first."
        );
        if (this.details?.uuid === hazardId) {
          await this.loadDetails(hazardId);
        }
        return false;
      }
      Logger.error('Failed to edit hazard', e);
      this.store.notification.enqueueErrorSnackbar('The hazard could not be updated. Something went wrong.');
      return false;
    }
  };

  archiveHazard = async (hazardId: string, archived: boolean): Promise<{ archivedAt: string | null } | undefined> => {
    try {
      const { data } = await (archived
        ? this.api.entities.archiveEntity(EntityModules.Hazards, hazardId)()
        : this.api.entities.unarchiveEntity(EntityModules.Hazards, hazardId)());
      this.store.notification.enqueueSuccessSnackbar(archived ? 'Hazard archived' : 'Hazard unarchived');
      return data;
    } catch (e) {
      Logger.error('Failed to change the archived state of a hazard', e);
      this.store.notification.enqueueErrorSnackbar(
        `The hazard could not be ${archived ? 'archived' : 'unarchived'}. Something went wrong.`
      );
      return undefined;
    }
  };

  @action.bound
  async setArchived(archived: boolean): Promise<boolean> {
    if (this.details?.uuid === undefined) {
      return false;
    }
    const hazardId = this.details.uuid;
    const data = await this.archiveHazard(hazardId, archived);
    if (!data) {
      return false;
    }
    runInAction(() => {
      if (this.details?.uuid === hazardId) {
        this.details = { ...this.details, archivedAt: data.archivedAt };
      }
    });
    return true;
  }

  @subscribe(EventType.LoggedOut)
  @action
  reset(): void {
    this.isLoaded = false;
    this.error = undefined;
    this.details = undefined;
    this.fieldErrors = {};
    this.deleteBlockedReason = undefined;
  }

  @action.bound
  contentHasBeenEdited(questionId: string, newValue: AnswerTypes): void {
    const question = this.details?.entity[questionId];
    this.isContentEdited = question?.value !== newValue;
  }
}
